import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, Circle, ShieldCheck, Sparkles, LogOut,
  Building2, ArrowRight, Lock, Phone,
} from "lucide-react";
import { api } from "../lib/api";
import { usePortalAuth } from "../context/PortalAuth";

export default function PortalWelcome() {
  const { user, logout } = usePortalAuth();
  const nav = useNavigate();

  const [data, setData] = useState(null);
  const [loadingStep, setLoadingStep] = useState(null); // "identity" | "profile" | "finish"
  const [openStep, setOpenStep] = useState(0);

  // Inline profile form state
  const [bizName, setBizName] = useState("");
  const [phone, setPhone] = useState("");

  // Local "acknowledged" flag for the intro step.
  const [introAck, setIntroAck] = useState(() => {
    try { return localStorage.getItem("elevare_intro_ack") === "1"; } catch { return false; }
  });

  const load = async () => {
    try {
      const { data: s } = await api.get("/portal/summary");
      setData(s);
      setBizName(s?.profile?.business_name || "");
      setPhone(s?.profile?.phone || "");
      // If already completed, jump straight into the dashboard.
      if (s?.user?.has_seen_tour) {
        nav("/portal", { replace: true });
      }
    } catch (e) {
      // Auth failure — bounce to login.
      if (e?.response?.status === 401) nav("/portal/login", { replace: true });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // When the user returns from the Stripe tab, auto-refresh identity status
  // so the checklist updates without a manual click.
  useEffect(() => {
    const onFocus = () => {
      const status = data?.identity?.status;
      if (!data?.identity?.verified && data?.identity?.session_started !== false) {
        // Silent refresh — no toast noise on every tab switch.
        api.get("/portal/identity/status").then(() => load()).catch(() => {});
      }
      // reference `status` so lint doesn't complain
      void status;
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [data]);

  const steps = useMemo(() => {
    const selfService = Boolean(data?.user?.self_service);
    const identityVerified = Boolean(data?.identity?.verified);
    const profileComplete = Boolean(
      (data?.profile?.business_name || "").trim() && (data?.profile?.phone || "").trim(),
    );
    const intro = {
      id: "intro",
      title: "Meet your team",
      blurb: selfService
        ? "Welcome to your self-service portal. Track your own expenses, jobs and finances here — and reach out to Kalib or John anytime you want us to review your numbers."
        : "Kalib and John personally review every plan we build for you. This dashboard is where you'll see recovery progress, savings, and the reports we ship.",
      done: introAck,
    };
    const identity = {
      id: "identity",
      title: "Verify your identity",
      blurb: "A one-time Stripe Identity check (~2 minutes). Your ID goes straight to Stripe — we never see or store it. Required before we handle any banking on your behalf.",
      done: identityVerified,
    };
    const profile = {
      id: "profile",
      title: "Complete your business profile",
      blurb: "Tell us the business name and a good phone number so we can reach you fast.",
      done: profileComplete,
    };
    // Self-service clients skip identity verification (we're not handling their banking).
    return selfService ? [intro, profile] : [intro, identity, profile];
  }, [data, introAck]);

  const completedCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const pct = Math.round((completedCount / total) * 100);
  const allDone = completedCount === total;

  const startIdentity = async () => {
    setLoadingStep("identity");
    try {
      const { data: res } = await api.post("/portal/identity/start");
      if (res?.url) {
        const win = window.open(res.url, "_blank", "noopener");
        if (win) toast.success("Opening Stripe Identity — return here when done.");
        else {
          try { await navigator.clipboard.writeText(res.url); } catch {}
          toast.info("Popup blocked — link copied. Paste it into a new tab.");
        }
      } else {
        toast.error("Stripe returned no URL — try again in a minute.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start verification.");
    } finally {
      setLoadingStep(null);
    }
  };

  const refreshIdentity = async () => {
    try {
      await api.get("/portal/identity/status");
      await load();
      toast.success("Status refreshed.");
    } catch {
      toast.error("Could not refresh identity status.");
    }
  };

  const saveProfile = async () => {
    if (!bizName.trim()) return toast.error("Business name is required.");
    if (!phone.trim()) return toast.error("Phone number is required.");
    setLoadingStep("profile");
    try {
      await api.post("/portal/onboarding/profile", {
        business_name: bizName.trim(), phone: phone.trim(),
      });
      await load();
      toast.success("Profile saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile.");
    } finally {
      setLoadingStep(null);
    }
  };

  const ackIntro = () => {
    try { localStorage.setItem("elevare_intro_ack", "1"); } catch {}
    setIntroAck(true);
    setOpenStep(1);
  };

  const finish = async () => {
    if (!allDone) return;
    setLoadingStep("finish");
    try {
      await api.post("/portal/tour/complete");
      toast.success("Welcome aboard.");
      nav("/portal", { replace: true });
    } catch {
      toast.error("Could not complete setup — try again.");
    } finally {
      setLoadingStep(null);
    }
  };

  const signOut = async () => { await logout(); nav("/portal/login", { replace: true }); };

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#ded9cb] text-[#1e3d3a]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const firstName = (data.user?.name || user?.name || "there").split(" ")[0];

  return (
    <div className="min-h-screen bg-[#ded9cb] text-[#1e3d3a] flex flex-col" data-testid="portal-welcome">
      {/* Header */}
      <header className="border-b border-black/10 bg-[#eae7de]/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.24em] text-[#a3823f]">Elevare · Client portal</span>
          <button
            type="button"
            onClick={signOut}
            data-testid="welcome-signout"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-black/45 hover:text-[#1e3d3a] transition-colors duration-300"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      {/* Hero + progress */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 lg:px-10 py-14">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Welcome</p>
          <h1 className="mt-3 font-head text-4xl lg:text-5xl font-medium tracking-tight">
            Let's get you set up, {firstName}.
          </h1>
          <p className="mt-4 text-sm sm:text-base text-black/65 leading-relaxed max-w-xl">
            Finish {total === 2 ? "these two quick steps" : "these three quick steps"} and your dashboard unlocks.
          </p>
        </motion.div>

        {/* Progress bar */}
        <div className="mt-8">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-black/45">
            <span data-testid="welcome-progress-label">{completedCount} of {total} complete</span>
            <span data-testid="welcome-progress-pct" className="tabular-nums">{pct}%</span>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-black/10 overflow-hidden">
            <motion.div
              data-testid="welcome-progress-bar"
              className="h-full rounded-full bg-gradient-to-r from-[#c9a961] to-[#a3823f]"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* Steps */}
        <ul className="mt-8 space-y-4" data-testid="welcome-steps">
          {steps.map((step, i) => (
            <StepCard
              key={step.id}
              stepNum={i + 1}
              step={step}
              open={openStep === i}
              onToggle={() => setOpenStep(openStep === i ? -1 : i)}
            >
              {step.id === "intro" && (
                <IntroStep done={step.done} onAck={ackIntro} />
              )}
              {step.id === "identity" && (
                <IdentityStep
                  done={step.done}
                  status={data?.identity?.status}
                  loading={loadingStep === "identity"}
                  onStart={startIdentity}
                  onRefresh={refreshIdentity}
                />
              )}
              {step.id === "profile" && (
                <ProfileStep
                  done={step.done}
                  bizName={bizName} setBizName={setBizName}
                  phone={phone} setPhone={setPhone}
                  onSave={saveProfile}
                  loading={loadingStep === "profile"}
                />
              )}
            </StepCard>
          ))}
        </ul>

        {/* Continue button */}
        <div className="mt-10">
          <button
            type="button"
            data-testid="welcome-continue"
            onClick={finish}
            disabled={!allDone || loadingStep === "finish"}
            className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm uppercase tracking-[0.22em] transition-all duration-300 ${
              allDone
                ? "bg-[#1e3d3a] text-white hover:bg-[#c9a961] hover:text-[#1e3d3a]"
                : "bg-black/10 text-black/30 cursor-not-allowed"
            }`}
          >
            {loadingStep === "finish" ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Loading dashboard…</>
            ) : allDone ? (
              <>Enter your dashboard <ArrowRight className="h-4 w-4" /></>
            ) : (
              <>Finish the steps above to continue <Lock className="h-3.5 w-3.5" /></>
            )}
          </button>
          <p className="mt-3 text-center text-[10px] uppercase tracking-[0.22em] text-black/40">
            Your progress saves as you go — you can close this page and return anytime.
          </p>
        </div>
      </main>
    </div>
  );
}

/* ---------- Presentational ---------- */

function StepCard({ stepNum, step, open, onToggle, children }) {
  return (
    <li
      data-testid={`welcome-step-${step.id}`}
      className={`rounded-2xl border overflow-hidden transition-colors duration-300 ${
        step.done ? "border-[#6b7c3a]/50 bg-white" : "border-black/12 bg-white"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`welcome-step-toggle-${step.id}`}
        className="w-full flex items-start gap-4 p-5 text-left hover:bg-[#c9a961]/6 transition-colors duration-200"
      >
        <div className="shrink-0 mt-0.5">
          {step.done ? (
            <CheckCircle2 className="h-6 w-6 text-[#6b7c3a]" strokeWidth={1.8} data-testid={`welcome-step-done-${step.id}`} />
          ) : (
            <Circle className="h-6 w-6 text-black/25" strokeWidth={1.5} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f]">Step {stepNum}</span>
            {step.done && (
              <span className="text-[9px] uppercase tracking-[0.22em] text-[#6b7c3a] bg-[#6b7c3a]/10 px-2 py-0.5 rounded-full">Done</span>
            )}
          </div>
          <h3 className="mt-1 font-head text-lg text-[#1e3d3a]">{step.title}</h3>
          <p className="mt-1 text-xs text-black/55 leading-relaxed">{step.blurb}</p>
        </div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="border-t border-black/8 bg-[#eae7de]/30"
          >
            <div className="p-5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

function IntroStep({ done, onAck }) {
  if (done) {
    return (
      <div className="text-xs text-black/55 flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-[#a3823f]" /> Introduction acknowledged.
      </div>
    );
  }
  return (
    <div className="space-y-3 text-sm text-black/70 leading-relaxed">
      <p>
        We&rsquo;re a two-person operation on purpose. Every recovery plan you see here was
        drafted, reviewed, and approved by one of us — not spun out by an AI without a human
        in the loop.
      </p>
      <p>You&rsquo;ll get real replies, real numbers, and a real phone number if something breaks.</p>
      <button
        type="button"
        onClick={onAck}
        data-testid="welcome-intro-ack"
        className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
      >
        Got it <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function IdentityStep({ done, status, loading, onStart, onRefresh }) {
  if (done) {
    return (
      <div className="text-xs text-[#4d5a24] flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" /> Identity verified. Secure banking is unlocked in the dashboard.
      </div>
    );
  }
  const isRequiresInput = status === "requires_input";
  const inProgress = ["processing", "created"].includes(status);
  return (
    <div className="space-y-3">
      <p className="text-sm text-black/70 leading-relaxed">
        Have a government ID handy (driver&rsquo;s licence or passport) and a device with a camera.
        Stripe walks you through it — the whole thing takes about two minutes.
      </p>
      {isRequiresInput && (
        <div className="rounded-lg border border-[#c9a961]/50 bg-[#c9a961]/10 px-3 py-2 text-xs text-[#1e3d3a] leading-relaxed">
          <strong>Your last verification wasn&rsquo;t completed.</strong> Click below to start a
          fresh session. If you keep getting stuck, email
          {" "}<a href="mailto:kalib@elevaresolutions.me" className="underline text-[#a3823f]">kalib@elevaresolutions.me</a>{" "}
          and we&rsquo;ll verify you manually.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onStart}
          disabled={loading}
          data-testid="welcome-identity-start"
          className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lock className="h-3.5 w-3.5" />}
          {isRequiresInput ? "Start fresh session" : (inProgress ? "Continue verification" : "Verify with Stripe")}
        </button>
        {(isRequiresInput || inProgress) && (
          <button
            type="button"
            onClick={onRefresh}
            data-testid="welcome-identity-refresh"
            className="text-[11px] uppercase tracking-[0.22em] text-black/50 hover:text-[#1e3d3a] transition-colors duration-300"
          >
            Refresh status
          </button>
        )}
        {inProgress && (
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f]">In review · {status}</span>
        )}
      </div>
    </div>
  );
}

function ProfileStep({ done, bizName, setBizName, phone, setPhone, onSave, loading }) {
  const inputCls = "w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] placeholder:text-black/30 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20 transition-colors duration-300";
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-black/45 flex items-center gap-1.5">
            <Building2 className="h-3 w-3" /> Business name
          </label>
          <input
            type="text"
            value={bizName}
            onChange={(e) => setBizName(e.target.value)}
            placeholder="e.g. Ridge & Rowan Cafe"
            className={"mt-1 " + inputCls}
            data-testid="welcome-profile-biz"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-black/45 flex items-center gap-1.5">
            <Phone className="h-3 w-3" /> Phone
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 555-5555"
            className={"mt-1 " + inputCls}
            data-testid="welcome-profile-phone"
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onSave}
          disabled={loading}
          data-testid="welcome-profile-save"
          className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
          {done ? "Update profile" : "Save profile"}
        </button>
        {done && <span className="text-[10px] uppercase tracking-[0.22em] text-[#4d5a24]">Saved ✓</span>}
      </div>
    </div>
  );
}
